'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getApiClient, getStoredTokens, setStoredTokens, clearStoredTokens } from 'saletrax-shared/clients/apiClient';
import { useStore } from './storeContext';

interface MarketplaceUser {
  id: string;
  /** Alias for `id` — backward compatibility with Firebase Auth's user.uid */
  uid: string;
  email: string;
  displayName?: string;
  accountType?: string;
  organizationId?: string;
}

interface AuthContextType {
  user: MarketplaceUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  customerData?: {
    signedUp: boolean;
    favorites: Array<{ id: string; type: 'product' | 'recipe'; addedAt: any }>;
    profilePic?: string;
  };
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<MarketplaceUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState<AuthContextType['customerData']>(undefined);
  const store = useStore();
  const api = getApiClient();

  // Restore session from stored tokens on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const { accessToken } = getStoredTokens();
        if (!accessToken) {
          setLoading(false);
          return;
        }

        const res = await api.get('/auth/me');
        const profile = res.data.data;
        setUser({
          id: profile.id,
          uid: profile.id,
          email: profile.email,
          displayName: profile.displayName,
          accountType: profile.accountType,
          organizationId: profile.organizationId,
        });
      } catch {
        // Token invalid or expired — clear and stay logged out
        clearStoredTokens();
        setUser(null);
        setCustomerData(undefined);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  // Listen for auth expired events (from apiClient auto-refresh failure)
  useEffect(() => {
    const handleExpired = () => {
      setUser(null);
      setCustomerData(undefined);
    };
    window.addEventListener('saletrax:auth:expired', handleExpired);
    return () => window.removeEventListener('saletrax:auth:expired', handleExpired);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const vendorOrganizationId = store.organizationId;
    if (!vendorOrganizationId) {
      throw new Error('Marketplace configuration error. Please contact support.');
    }

    const res = await api.post('/auth/marketplace/login', {
      email,
      password,
      vendorOrganizationId,
    });

    const { user: userData, tokens } = res.data.data;
    setStoredTokens(tokens.accessToken, tokens.refreshToken);
    setUser({ ...userData, uid: userData.id });
  }, [store.organizationId, api]);

  const logout = useCallback(async () => {
    try {
      const { refreshToken } = getStoredTokens();
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch {
      // Logout endpoint failed — clear tokens anyway
    } finally {
      clearStoredTokens();
      setUser(null);
      setCustomerData(undefined);
    }
  }, [api]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, customerData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
